import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { fetchDispatch } from 'data/helpers'
import { showSnackbar, snackbarTypes } from 'src/redux/snackbar'
import { useDispatch } from 'react-redux'

const SynonymTable = ({ items, onEdit }) => (
  <TableContainer>
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold', width: 300 }}>Name</TableCell>
          <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Actions</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>Synonyms</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map(item => (
          <TableRow key={item.id}>
            <TableCell>
              <Typography variant='body2'>{item.name}</Typography>
            </TableCell>
            <TableCell>
              <Button
                variant='outlined'
                size='small'
                onClick={() => onEdit(item)}
              >
                Edit
              </Button>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {(item.synonyms || []).map(synonym => (
                  <Chip key={synonym} label={synonym} size='small' />
                ))}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const EditSynonymsDialog = ({ open, onClose, item, type, onSave }) => {
  const [synonymsText, setSynonymsText] = useState('')
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')

  useEffect(() => {
    if (!item || !open) return

    const fetchLatest = async () => {
      setLoading(true)
      try {
        const path = type === 'specialty'
          ? `/api/specialty_synonyms/specialties/${item.id}`
          : `/api/specialty_synonyms/subspecialties/${item.id}`
        const result = await fetchDispatch({ path, method: 'GET' })
        setSynonymsText((result.synonyms || []).join(', '))
        setName(result.name)
      } catch {
        setSynonymsText((item.synonyms || []).join(', '))
        setName(item.name)
      }
      setLoading(false)
    }

    fetchLatest()
  }, [item, open, type])

  const handleSave = () => {
    const synonyms = synonymsText
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    onSave(item.id, synonyms)
  }

  if (!item) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Edit Synonyms: {name || item.name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Enter synonyms separated by commas. These are alternative names that will be used to match this {type} during AI search.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label='Synonyms'
          placeholder='e.g. Cards, Cardio'
          value={synonymsText}
          onChange={e => setSynonymsText(e.target.value)}
          helperText={`${synonymsText.split(',').map(s => s.trim()).filter(Boolean).length} synonym(s)`}
        />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleSave} disabled={loading}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

const SpecialtySynonyms = () => {
  const dispatch = useDispatch()
  const [tab, setTab] = useState(0)
  const [specialties, setSpecialties] = useState([])
  const [subspecialties, setSubspecialties] = useState([])
  const [loading, setLoading] = useState(true)
  const [editItem, setEditItem] = useState(null)
  const [editType, setEditType] = useState(null)

  const fetchData = async () => {
    setLoading(true)
    try {
      const [specResult, subspecResult] = await Promise.all([
        fetchDispatch({ path: '/api/specialty_synonyms/specialties', method: 'GET' }),
        fetchDispatch({ path: '/api/specialty_synonyms/subspecialties', method: 'GET' }),
      ])
      setSpecialties(specResult)
      setSubspecialties(subspecResult)
    } catch {
      dispatch(showSnackbar('Failed to load synonyms', snackbarTypes.ERROR))
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleEdit = (item, type) => {
    setEditItem(item)
    setEditType(type)
  }

  const handleSave = async (id, synonyms) => {
    const path = editType === 'specialty'
      ? `/api/specialty_synonyms/specialties/${id}`
      : `/api/specialty_synonyms/subspecialties/${id}`

    try {
      await fetchDispatch({
        path,
        method: 'PUT',
        values: { synonyms },
      })
      dispatch(showSnackbar('Synonyms updated. Embeddings will regenerate in the background.', snackbarTypes.SUCCESS))
      setEditItem(null)
      fetchData()
    } catch {
      dispatch(showSnackbar('Failed to update synonyms', snackbarTypes.ERROR))
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' sx={{ mb: 3 }}>Specialty Synonyms</Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label='Specialties' />
        <Tab label='Subspecialties' />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && (
            <SynonymTable
              items={specialties}
              type='specialty'
              onEdit={item => handleEdit(item, 'specialty')}
            />
          )}
          {tab === 1 && (
            <SynonymTable
              items={subspecialties}
              type='subspecialty'
              onEdit={item => handleEdit(item, 'subspecialty')}
            />
          )}
        </>
      )}

      <EditSynonymsDialog
        open={!!editItem}
        onClose={() => setEditItem(null)}
        item={editItem}
        type={editType}
        onSave={handleSave}
      />
    </Box>
  )
}

export default SpecialtySynonyms
